import { createAction } from "@reduxjs/toolkit";
import { t } from "ttag";

import { setupApi, userApi } from "metabase/api";
import { loadLocalization } from "metabase/api/localization";
import { isEmailAlreadyInUse } from "metabase/api/utils/errors";
import { runRtkEndpoint } from "metabase/api/utils/run-rtk-endpoint";
import { trackUserInvited } from "metabase/common/analytics";
import { refetchCurrentUser } from "metabase/current-user";
import { createDatabase } from "metabase/redux/databases";
import type {
  InviteInfo,
  Locale,
  SetupStep,
  State,
  UserInfo,
} from "metabase/redux/store";
import { createAsyncThunk } from "metabase/redux/utils";
import {
  getSetting,
  refetchSiteSettings,
  settingsApi,
} from "metabase/settings";
import MetabaseSettings from "metabase/utils/settings";
import type { DatabaseData, Settings, UsageReason } from "metabase-types/api";

import {
  trackAddDataLaterClicked,
  trackAiProviderConnected,
  trackAiSetupLaterClicked,
  trackAiSetupStarted,
  trackDatabaseSelected,
  trackLicenseTokenStepSubmitted,
  trackTrackingChanged,
  trackUsageReasonSelected,
} from "./analytics";
import {
  getIsEmbeddingUseCase,
  getLocale,
  getNextStep,
  getUsageReason,
} from "./selectors";
import { getDefaultLocale, getLocales } from "./utils";

interface ThunkConfig {
  state: State;
}

export const goToNextStep = createAsyncThunk(
  "metabase/setup/goToNextStep",
  async (_, { getState, dispatch }) => {
    const state = getState();
    const nextStep = getNextStep(state);
    dispatch(selectStep(nextStep));
    if (nextStep === "completed") {
      dispatch(setEmbeddingHomepageFlags());
    }
  },
);

export const LOAD_LOCALE_DEFAULTS = "metabase/setup/LOAD_LOCALE_DEFAULTS";
export const loadLocaleDefaults = createAsyncThunk<
  Locale | undefined,
  void,
  ThunkConfig
>(LOAD_LOCALE_DEFAULTS, async (_, { getState }) => {
  const data = getSetting(getState(), "available-locales");
  const locale = getDefaultLocale(getLocales(data));
  if (locale) {
    await loadLocalization(locale.code);
  }
  return locale;
});

export const LOAD_DEFAULTS = "metabase/setup/LOAD_DEFAULTS";
export const loadDefaults = createAsyncThunk<void, void, ThunkConfig>(
  LOAD_DEFAULTS,
  (_, { dispatch }) => {
    dispatch(loadLocaleDefaults());
  },
);

export const SELECT_STEP = "metabase/setup/SUBMIT_WELCOME_STEP";
export const selectStep = createAction<SetupStep>(SELECT_STEP);

export const UPDATE_LOCALE = "metabase/setup/UPDATE_LOCALE";
export const updateLocale = createAsyncThunk(
  UPDATE_LOCALE,
  async (locale: Locale) => {
    await loadLocalization(locale.code);
  },
);

export const submitUser = createAsyncThunk<void, UserInfo, ThunkConfig>(
  "metabase/setup/SUBMIT_USER_INFO",
  async (user: UserInfo, { dispatch, getState, rejectWithValue }) => {
    const token = getSetting(getState(), "setup-token");
    const locale = getLocale(getState());

    try {
      await runRtkEndpoint(
        {
          token,
          user,
          prefs: {
            site_name: user.site_name,
            site_locale: locale?.code,
          },
        },
        dispatch,
        setupApi.endpoints.createSetup,
      );
    } catch (error) {
      return rejectWithValue(error);
    }

    MetabaseSettings.set("setup-token", null);
    dispatch(goToNextStep());
    //  load the settings after the user is logged, needed later by setEmbeddingHomepageFlags
    dispatch(refetchSiteSettings());
    //  the AI config step needs to know the created user is an admin
    dispatch(refetchCurrentUser());
  },
);

export const submitUsageReason = createAsyncThunk(
  "metabase/setup/SUBMIT_USAGE_REASON",
  (usageReason: UsageReason, { dispatch }) => {
    trackUsageReasonSelected(usageReason);
    dispatch(goToNextStep());
  },
);

export const UPDATE_DATABASE_ENGINE = "metabase/setup/UPDATE_DATABASE_ENGINE";
export const updateDatabaseEngine = createAsyncThunk(
  UPDATE_DATABASE_ENGINE,
  (engine?: string) => {
    if (engine) {
      trackDatabaseSelected(engine);
    }
  },
);

export const SUBMIT_DATABASE = "metabase/setup/SUBMIT_DATABASE";
export const submitDatabase = createAsyncThunk<
  DatabaseData,
  DatabaseData,
  ThunkConfig
>(
  SUBMIT_DATABASE,
  async (database: DatabaseData, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(createDatabase(database));
      dispatch(goToNextStep());
      return database;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const SKIP_DATABASE = "metabase/setup/SKIP_DATABASE";
export const skipDatabase = createAsyncThunk(
  SKIP_DATABASE,
  (engine: string | undefined, { dispatch }) => {
    trackAddDataLaterClicked(engine);
    dispatch(goToNextStep());
  },
);

export const SUBMIT_USER_INVITE = "metabase/setup/SUBMIT_USER_INVITE";
export const submitUserInvite = createAsyncThunk(
  SUBMIT_USER_INVITE,
  async (inviteInfo: InviteInfo, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(
        userApi.endpoints.createUser.initiate({
          email: inviteInfo.email,
          first_name: inviteInfo.first_name || undefined,
          last_name: inviteInfo.last_name || undefined,
          source: "setup",
        }),
      ).unwrap();
      trackUserInvited({
        triggeredFrom: "setup",
        targetId: null,
        result: "success",
        eventDetail: "new_user",
      });
      dispatch(goToNextStep());
    } catch (error) {
      trackUserInvited({
        triggeredFrom: "setup",
        targetId: null,
        result: "failure",
        eventDetail: isEmailAlreadyInUse(error) ? "existing_user" : null,
      });
      return rejectWithValue(error);
    }
  },
);

export const START_AI_CONFIG = "metabase/setup/START_AI_CONFIG";
export const startAiConfig = createAsyncThunk(
  START_AI_CONFIG,
  (_: void, { dispatch }) => {
    trackAiSetupStarted();
    dispatch(selectStep("ai_config"));
  },
);

export const SUBMIT_AI_CONFIG = "metabase/setup/SUBMIT_AI_CONFIG";
export const submitAiConfig = createAsyncThunk(
  SUBMIT_AI_CONFIG,
  (provider: string | undefined, { dispatch }) => {
    trackAiProviderConnected(provider);
    dispatch(selectStep("completed"));
  },
);

export const SKIP_AI_CONFIG = "metabase/setup/SKIP_AI_CONFIG";
export const skipAiConfig = createAsyncThunk(
  SKIP_AI_CONFIG,
  (_: void, { dispatch }) => {
    trackAiSetupLaterClicked();
    dispatch(selectStep("completed"));
  },
);

export const submitLicenseToken = createAsyncThunk(
  "metabase/setup/SUBMIT_LICENSE_TOKEN",
  async (licenseToken: string | null, { dispatch, rejectWithValue }) => {
    try {
      if (licenseToken) {
        await dispatch(
          settingsApi.endpoints.updateSetting.initiate({
            key: "premium-embedding-token",
            value: licenseToken,
          }),
        ).unwrap();
      }
      trackLicenseTokenStepSubmitted(Boolean(licenseToken));
    } catch (err) {
      return rejectWithValue(
        t`This token doesn't seem to be valid. Double-check it, then contact support if you think it should be working.`,
      );
    }

    dispatch(goToNextStep());
  },
);

export const UPDATE_TRACKING = "metabase/setup/UPDATE_TRACKING";
export const updateTracking = createAsyncThunk(
  UPDATE_TRACKING,
  async (isTrackingAllowed: boolean, { dispatch, rejectWithValue }) => {
    try {
      await dispatch(
        settingsApi.endpoints.updateSetting.initiate({
          key: "anon-tracking-enabled",
          value: isTrackingAllowed,
        }),
      ).unwrap();
      trackTrackingChanged(isTrackingAllowed);
      MetabaseSettings.set("anon-tracking-enabled", isTrackingAllowed);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const setEmbeddingHomepageFlags = createAsyncThunk(
  "setup/setEmbeddingHomepageFlags",
  async (_, { getState, dispatch }) => {
    const usageReason = getUsageReason(getState());
    const tokenFeatures = getSetting(getState(), "token-features");
    const isEmbeddingUseCase = getIsEmbeddingUseCase(getState());

    const interestedInEmbedding =
      usageReason === "embedding" || usageReason === "both";
    const isLicenseActive = tokenFeatures && tokenFeatures["embedding"];

    const settingsToChange: Partial<Settings> = {};

    if (isEmbeddingUseCase || interestedInEmbedding) {
      settingsToChange["embedding-homepage"] = "visible";
    }

    if (interestedInEmbedding) {
      settingsToChange["setup-embedding-autoenabled"] = true;
    }

    settingsToChange["setup-license-active-at-setup"] = isLicenseActive;

    dispatch(settingsApi.endpoints.updateSettings.initiate(settingsToChange));
  },
);
